import { Dispatch } from 'redux'
import config from 'app/config/config'
import { client } from 'app/shared/api/client'
import {
  CreateFundGroupInput,
  FundGroup,
  UpdateFundGroupInput,
} from 'app/models/fund-group'
import {
  loadFundGroupsAction,
  loadFundGroupsForManagerAction,
  removeFundGroup,
  setFundGroupsLoading,
  upsertFundGroup,
} from './actions'

const baseUrl = `${config.backendUrl}/api/fund-groups`

export type FundGroupThunkResult<T = FundGroup> =
  | { success: true; payload: T }
  | { success: false; status?: number; payload: string }

const failure = (e: any, fallback: string): FundGroupThunkResult<never> => ({
  success: false,
  status: e?.response?.status,
  payload: e?.response?.data?.message ?? fallback,
})

/**
 * Latest request token per fund manager. A by-manager load that finishes AFTER a newer load for
 * the same manager was started is superseded: its (stale) groups must not overwrite the newer
 * ones, so only the current request may dispatch into the store.
 */
const latestManagerRequest = new Map<string, number>()
let requestSeq = 0

const beginManagerRequest = (fundManagerId: string): number => {
  const token = ++requestSeq
  latestManagerRequest.set(fundManagerId, token)
  return token
}

const isCurrentManagerRequest = (fundManagerId: string, token: number) =>
  latestManagerRequest.get(fundManagerId) === token

/** Groups visible to the caller, optionally only one fund manager's. A FAILED load must not
 * touch the store: dispatching [] would blank groups that were correct a moment ago. A load
 * filtered to one manager replaces only THAT manager's groups, keeping other managers' cached. */
export const loadFundGroups =
  (fundManagerId?: string) => async (dispatch: Dispatch) => {
    dispatch(setFundGroupsLoading(true))
    try {
      const res = await client.get(baseUrl, {
        params: fundManagerId ? { fundManagerId } : undefined,
      })
      const groups = (res.data as unknown as FundGroup[]) ?? []
      dispatch(
        fundManagerId
          ? loadFundGroupsForManagerAction(fundManagerId, groups)
          : loadFundGroupsAction(groups),
      )
      return { success: true as const, payload: groups }
    } catch (e: any) {
      return failure(e, 'Failed to load allocation groups')
    } finally {
      dispatch(setFundGroupsLoading(false))
    }
  }

/**
 * GET /api/fund-groups/by-manager/:fundManagerId — one fund manager's groups, already filtered to
 * what the caller may see (all Public + own Private). This is what the trade editor uses.
 */
export const loadFundGroupsByManager =
  (fundManagerId: string) => async (dispatch: Dispatch) => {
    if (!fundManagerId) {
      return { success: false as const, payload: 'No fund manager id' }
    }
    const token = beginManagerRequest(fundManagerId)
    dispatch(setFundGroupsLoading(true))
    try {
      const res = await client.get(`${baseUrl}/by-manager/${fundManagerId}`)
      const groups = (res.data as unknown as FundGroup[]) ?? []
      // Superseded by a newer load for this manager: hand the data back but leave the store to
      // the request that is still current.
      if (isCurrentManagerRequest(fundManagerId, token)) {
        dispatch(loadFundGroupsForManagerAction(fundManagerId, groups))
      }
      return { success: true as const, payload: groups }
    } catch (e: any) {
      return failure(e, 'Failed to load allocation groups')
    } finally {
      if (isCurrentManagerRequest(fundManagerId, token)) {
        dispatch(setFundGroupsLoading(false))
      }
    }
  }

export const createFundGroup =
  (input: CreateFundGroupInput) => async (dispatch: Dispatch) => {
    try {
      const res = await client.post(baseUrl, input)
      const created = res.data as unknown as FundGroup
      dispatch(upsertFundGroup(created))
      return { success: true as const, payload: created }
    } catch (e: any) {
      return failure(e, 'Failed to create allocation group')
    }
  }

export const updateFundGroup =
  (id: string, input: UpdateFundGroupInput) => async (dispatch: Dispatch) => {
    try {
      const res = await client.put(`${baseUrl}/${id}`, input)
      const updated = res.data as unknown as FundGroup
      dispatch(upsertFundGroup(updated))
      return { success: true as const, payload: updated }
    } catch (e: any) {
      return failure(e, 'Failed to update allocation group')
    }
  }

export const deleteFundGroup = (id: string) => async (dispatch: Dispatch) => {
  try {
    await client.delete(`${baseUrl}/${id}`)
    dispatch(removeFundGroup(id))
    return { success: true as const, payload: id }
  } catch (e: any) {
    return failure(e, 'Failed to delete allocation group')
  }
}
